import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  IconButton,
  Button,
  TextField,
  Divider,
  Alert,
} from '@mui/material';
import { Delete, Add, Remove, ShoppingBag } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import Loading from '../components/Loading';

const Cart = () => {
  const navigate = useNavigate();
  const { cart, loading, updateCartItem, removeFromCart } = useCart();
  const [updating, setUpdating] = useState({});

  const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setUpdating({ ...updating, [itemId]: true });
    try {
      await updateCartItem(itemId, newQuantity);
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      setUpdating({ ...updating, [itemId]: false });
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await removeFromCart(itemId);
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  if (loading) return <Loading />;

  if (!cart.items || cart.items.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <ShoppingBag sx={{ fontSize: 100, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Your cart is empty
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Add some products to get started!
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/products')}
        >
          Browse Products
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        Shopping Cart
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {cart.items.map((item) => {
            const imageUrl = item.image ? `${API_URL}${item.image}` : '/placeholder-product.jpg';
            
            return (
              <Paper key={item.id} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={3} sm={2}>
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={item.name}
                      sx={{ width: '100%', borderRadius: 1 }}
                      onError={(e) => e.target.src = '/placeholder-product.jpg'}
                    />
                  </Grid>

                  <Grid item xs={9} sm={4}>
                    <Typography variant="h6" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/products/${item.product_id}`)}>
                      {item.name}
                    </Typography>
                    {item.sku && (
                      <Typography variant="caption" color="text.secondary">
                        SKU: {item.sku}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1 || updating[item.id]}
                      >
                        <Remove />
                      </IconButton>
                      
                      <TextField
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (val > 0 && val <= item.stock_quantity) {
                            handleQuantityChange(item.id, val);
                          }
                        }}
                        inputProps={{ min: 1, max: item.stock_quantity }}
                        sx={{ width: 60 }}
                        size="small"
                        disabled={updating[item.id]}
                      />
                      
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        disabled={item.quantity >= item.stock_quantity || updating[item.id]}
                      >
                        <Add />
                      </IconButton>
                    </Box>
                    {item.quantity >= item.stock_quantity && (
                      <Typography variant="caption" color="error">
                        Max stock reached
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={6} sm={2}>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      ${(item.price * item.quantity).toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ${item.price} each
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={1}>
                    <IconButton
                      color="error"
                      onClick={() => handleRemove(item.id)}
                    >
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 80 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Order Summary
            </Typography>
            
            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal ({cart.itemCount} items)</Typography>
                <Typography>${cart.total.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Shipping</Typography>
                <Typography color="success.main">FREE</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">Total</Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                ${cart.total.toFixed(2)}
              </Typography>
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => navigate('/checkout')}
            >
              Proceed to Checkout
            </Button>

            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
              onClick={() => navigate('/products')}
            >
              Continue Shopping
            </Button>

            <Alert severity="info" sx={{ mt: 2 }}>
              Free shipping on all orders!
            </Alert>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Cart;
